import React from 'react';

import CSSModules from 'react-css-modules';

import FullScreenImageButton from '../../../FullScreenImageButton';
import {resolved, usePreloadImage} from '../../../hooks/preloadImage';
import {useWindowSize} from '../../../hooks/windowSize';
import {getImageRatio, isRatioInTolerance} from '../../../utils/imageUtils';

import styles from '../styles.module.scss';

type ImageContentProps = {
  image: {
    url: string;
    position: 'top' | 'bottom';
  };
  style?: {[key: string]: string};
  editMode?: boolean;
};

// eslint-disable-next-line complexity
const ImageContent = ({
  image,
  style = {},
  editMode = false,
}: ImageContentProps): JSX.Element => {
  const IMAGE_FRAME_RATIO = 5 / 4;

  const fullImage = usePreloadImage(image.url);
  const isImageResolved = fullImage.status === resolved;
  const imageRatio = isImageResolved
    ? getImageRatio(fullImage.width, fullImage.height)
    : null;

  const hasRatioInToleranceOrUnresolved =
    !isImageResolved || isRatioInTolerance(imageRatio, IMAGE_FRAME_RATIO);

  const additionalStyles = {
    backgroundImage: `url(${image.url})`,
    backgroundSize: hasRatioInToleranceOrUnresolved ? 'contain' : 'cover',
    backgroundPosition: 'center',
  };

  const windowSize = useWindowSize();

  if (windowSize.width < 720 || editMode) {
    // eslint-disable-next-line no-nested-ternary
    additionalStyles.backgroundPosition = isRatioInTolerance(
      imageRatio,
      IMAGE_FRAME_RATIO
    )
      ? image.position === 'top'
        ? 'top'
        : 'bottom'
      : 'center';

    const imageFrameStyles = !editMode
      ? {
          height: windowSize.width / IMAGE_FRAME_RATIO,
          width: windowSize.width,
        }
      : {};

    return (
      <FullScreenImageButton disabled={editMode} image={fullImage}>
        <div
          styleName='fixed-ratio-image'
          data-automation={`slide-image${editMode ? '-preview' : ''}`}
          style={{...additionalStyles, ...imageFrameStyles, ...style}}
        />
      </FullScreenImageButton>
    );
  } else {
    return (
      <div styleName='half-container image-container'>
        <FullScreenImageButton image={fullImage}>
          <div
            styleName='fixed-ratio-image'
            data-automation={`slide-image${editMode ? '-preview' : ''}`}
            style={additionalStyles}
          />
        </FullScreenImageButton>
      </div>
    );
  }
};

export default CSSModules(ImageContent, styles, {allowMultiple: true});
